// One WebGL2 canvas for the whole site. Scenes are lazy-loaded modules that
// draw into an offscreen framebuffer; the engine adds bloom, chromatic
// aberration, C64 "tape loading" stripes, flashes, CRT and grain on top.

import { HDR, VS_FULLSCREEN, Fbo, Program, U, deleteFbo, makeFbo, makeTexture, program, clamp, lerp } from './gl';

export interface FrameCtx {
  gl: WebGL2RenderingContext;
  e: Engine;
  /** Seconds since the scene started. */
  t: number;
  dt: number;
  W: number;
  H: number;
  /** Beat position of the current music (float) and a 0..1 kick pulse. */
  beat: number;
  kick: number;
  /** 0..1 pulse on every keypress in the terminal. */
  key: number;
  /** 1280x720 overlay canvas, cleared each frame. Return true from frame() if you drew on it. */
  text: CanvasRenderingContext2D;
}

export interface Scene {
  /** Internal resolution multiplier (heavy shaders render smaller). */
  scale?: number;
  bloom?: number;
  crt?: number;
  nearest?: boolean;
  init(e: Engine): void;
  frame(f: FrameCtx): boolean | void;
  dispose(e: Engine): void;
}

export type SceneFactory = () => Promise<{ default: () => Scene }>;

export interface MusicClock { beat(): number; kick(): number }

const FS_BRIGHT = `#version 300 es
precision highp float; in vec2 vUV; uniform sampler2D SRC; uniform float TH; out vec4 O;
void main(){vec3 c=texture(SRC,vUV).rgb;float l=max(c.r,max(c.g,c.b));O=vec4(c*smoothstep(TH,TH+.35,l),1.);}`;
const FS_BLUR = `#version 300 es
precision highp float; in vec2 vUV; uniform sampler2D SRC; uniform vec2 DIR; out vec4 O;
void main(){vec3 c=texture(SRC,vUV).rgb*.227;
  c+=(texture(SRC,vUV+DIR*1.385).rgb+texture(SRC,vUV-DIR*1.385).rgb)*.316;
  c+=(texture(SRC,vUV+DIR*3.231).rgb+texture(SRC,vUV-DIR*3.231).rgb)*.07;O=vec4(c,1.);}`;
const FS_POST = HDR + `
in vec2 vUV; uniform sampler2D SC,BL,TX; uniform float TXON,FLASH,FADE,CA,CRT,BS,LD; uniform vec2 TXS;
const vec3 C64[8]=vec3[8](vec3(.53,.22,.2),vec3(.4,.75,.78),vec3(.55,.25,.59),vec3(.33,.64,.26),vec3(.25,.19,.55),vec3(.75,.81,.44),vec3(.55,.33,.16),vec3(.72,.41,.4));
void main(){
  vec2 uv=vUV;
  if(CRT>0.){vec2 c=uv*2.-1.;c*=1.+CRT*.06*(c.yx*c.yx);uv=c*.5+.5;
    if(uv.x<0.||uv.y<0.||uv.x>1.||uv.y>1.){O=vec4(0,0,0,1);return;}}
  vec2 dir=(uv-.5)*CA*.012;
  vec3 col=vec3(texture(SC,uv+dir).r,texture(SC,uv).g,texture(SC,uv-dir).b);
  col+=texture(BL,uv).rgb*BS;
  if(TXON>0.){vec2 tuv=(uv-.5)*TXS+.5;
    if(tuv.x>0.&&tuv.y>0.&&tuv.x<1.&&tuv.y<1.){vec4 t=texture(TX,vec2(tuv.x,1.-tuv.y));col=mix(col,t.rgb,t.a);}}
  if(LD>0.){float band=floor((gl_FragCoord.y+floor(T*50.)*37.)/(5.+14.*h11(floor(T*50.))));
    col=mix(col,C64[int(h11(band+floor(T*25.))*7.99)],LD*.6);}
  col+=FLASH;
  vec2 vg=uv-.5; col*=1.-.7*dot(vg,vg);
  if(CRT>0.) col*=mix(1.,.8+.2*sin(uv.y*R.y*PI*.5),CRT*.6);
  col+=(h21(gl_FragCoord.xy+fract(T)*100.)-.5)*.03;
  O=vec4(col*FADE,1.);
}`;

export class Engine {
  readonly gl: WebGL2RenderingContext;
  readonly canvas: HTMLCanvasElement;
  readonly vao: WebGLVertexArrayObject;
  private post: Program; private bright: Program; private blur: Program;
  private fbos: Record<string, Fbo> = {};
  private textCanvas: HTMLCanvasElement; private text: CanvasRenderingContext2D; private textTex: WebGLTexture;
  private scene: Scene | null = null; private sceneName = ''; private sceneStart = 0; private loadToken = 0;
  private registry: Record<string, SceneFactory> = {};
  private last = 0; private avgDt = 16; private slow = 0; private fast = 0;
  private flashV = 0; private loaderUntil = 0; private keyV = 0; private fade = 0;
  quality = 1;
  crt = true;
  reducedMotion = false;
  clock: MusicClock = { beat: () => performance.now() / 500, kick: () => 0 };
  private raf = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: false, powerPreference: 'high-performance' });
    if (!gl) throw new Error('WebGL2 not available');
    this.gl = gl;
    this.vao = gl.createVertexArray()!;
    this.post = program(gl, VS_FULLSCREEN, FS_POST);
    this.bright = program(gl, VS_FULLSCREEN, FS_BRIGHT);
    this.blur = program(gl, VS_FULLSCREEN, FS_BLUR);
    this.textCanvas = document.createElement('canvas');
    this.textCanvas.width = 1280; this.textCanvas.height = 720;
    this.text = this.textCanvas.getContext('2d')!;
    this.textTex = makeTexture(gl, 1280, 720);
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    canvas.addEventListener('webglcontextlost', (e) => e.preventDefault());
  }

  register(name: string, f: SceneFactory) { this.registry[name] = f; }

  /** Switch scene. Resolves once the new scene is running. */
  async setScene(name: string) {
    if (name === this.sceneName) return;
    const token = ++this.loadToken;
    const factory = this.registry[name];
    if (!factory) { console.warn('unknown scene', name); return; }
    const mod = await factory();
    if (token !== this.loadToken) return; // a newer request won
    const next = mod.default();
    next.init(this);
    if (this.scene) this.scene.dispose(this);
    this.scene = next; this.sceneName = name; this.sceneStart = performance.now();
    this.flash(.35);
  }
  get currentScene() { return this.sceneName; }

  flash(a: number) { if (!this.reducedMotion) this.flashV = Math.max(this.flashV, a); }
  loader(seconds: number) { if (!this.reducedMotion) this.loaderUntil = performance.now() + seconds * 1000; }
  keyPulse() { this.keyV = 1; }

  /** Cached program per scene; scenes own their programs and delete them in dispose(). */
  prog(fs: string, vs: string = VS_FULLSCREEN) { return program(this.gl, vs, fs); }
  u(p: Program) { this.gl.useProgram(p.p); return new U(this.gl, p); }
  /** Draw a fullscreen triangle with the current program, setting the common uniforms. */
  fullscreen(p: Program, f: FrameCtx) {
    this.common(p, f);
    this.gl.bindVertexArray(this.vao);
    this.gl.drawArrays(this.gl.TRIANGLES, 0, 3);
  }
  common(p: Program, f: FrameCtx) {
    return this.u(p).v2('R', f.W, f.H).f('T', f.t).f('K', f.kick).f('BT', f.beat).f('KP', f.key);
  }

  private fbo(name: string, w: number, h: number, depth = false) {
    const f = this.fbos[name];
    if (f && f.w === w && f.h === h) return f;
    if (f) deleteFbo(this.gl, f);
    return (this.fbos[name] = makeFbo(this.gl, w, h, depth));
  }

  start() {
    const loop = (now: number) => { this.raf = requestAnimationFrame(loop); this.frame(now); };
    this.raf = requestAnimationFrame(loop);
  }
  stop() { cancelAnimationFrame(this.raf); }

  private resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = Math.round(this.canvas.clientWidth * dpr), h = Math.round(this.canvas.clientHeight * dpr);
    const k = Math.min(1, 1920 / Math.max(w, (h * 16) / 9));
    w = Math.max(2, Math.round(w * k)); h = Math.max(2, Math.round(h * k));
    if (this.canvas.width !== w || this.canvas.height !== h) { this.canvas.width = w; this.canvas.height = h; }
  }

  private frame(now: number) {
    const gl = this.gl;
    const dt = this.last ? Math.min(100, now - this.last) : 16; this.last = now;
    if (document.hidden) return;
    this.avgDt = lerp(this.avgDt, dt, .05);
    if (this.avgDt > 24) { this.slow += dt; this.fast = 0; if (this.slow > 1200) { this.quality = Math.max(.35, this.quality - .1); this.slow = 0; } }
    else if (this.avgDt < 15) { this.fast += dt; this.slow = 0; if (this.fast > 4000) { this.quality = Math.min(1, this.quality + .1); this.fast = 0; } }
    this.resize();

    const sc = this.scene;
    const scale = Math.round(Math.min(1, (sc?.scale ?? 1) * this.quality) * 20) / 20;
    const W = Math.max(2, Math.round(this.canvas.width * scale)), H = Math.max(2, Math.round(this.canvas.height * scale));
    const F = this.fbo('scene', W, H, true);
    gl.bindFramebuffer(gl.FRAMEBUFFER, F.f); gl.viewport(0, 0, W, H);
    gl.disable(gl.DEPTH_TEST); gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 1); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    this.keyV *= Math.pow(.02, dt / 1000);
    this.flashV *= Math.pow(.004, dt / 1000);
    this.fade = Math.min(1, this.fade + dt / 600);
    const motion = this.reducedMotion ? .15 : 1;
    const f: FrameCtx = {
      gl, e: this, t: (now - this.sceneStart) / 1000 * motion, dt: dt / 1000, W, H,
      beat: this.clock.beat(), kick: this.reducedMotion ? 0 : this.clock.kick(), key: this.keyV, text: this.text,
    };
    this.text.clearRect(0, 0, 1280, 720);
    let txOn = false;
    if (sc) {
      try { txOn = !!sc.frame(f); } catch (err) { console.error(err); this.scene = null; }
    }
    gl.bindTexture(gl.TEXTURE_2D, F.t);
    const filt = sc?.nearest ? gl.NEAREST : gl.LINEAR;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filt); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filt);

    // bloom
    const bw = Math.max(2, W >> 2), bh = Math.max(2, H >> 2), b1 = this.fbo('b1', bw, bh), b2 = this.fbo('b2', bw, bh);
    gl.bindVertexArray(this.vao); gl.viewport(0, 0, bw, bh);
    gl.bindFramebuffer(gl.FRAMEBUFFER, b1.f); this.u(this.bright).tex('SRC', 0, F.t).f('TH', .5); gl.drawArrays(gl.TRIANGLES, 0, 3);
    for (const k of [1, 2.2]) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, b2.f); this.u(this.blur).tex('SRC', 0, b1.t).v2('DIR', k / bw, 0); gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindFramebuffer(gl.FRAMEBUFFER, b1.f); this.u(this.blur).tex('SRC', 0, b2.t).v2('DIR', 0, k / bh); gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    if (txOn) { gl.bindTexture(gl.TEXTURE_2D, this.textTex); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.textCanvas); }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    const ar = this.canvas.width / this.canvas.height, tar = 16 / 9;
    const ld = now < this.loaderUntil ? 1 : 0;
    this.u(this.post).v2('R', this.canvas.width, this.canvas.height).f('T', now / 1000)
      .tex('SC', 0, F.t).tex('BL', 1, b1.t).tex('TX', 2, this.textTex)
      .v2('TXS', ar > tar ? ar / tar : 1, ar > tar ? 1 : tar / ar)
      .f('TXON', txOn ? 1 : 0).f('FLASH', this.flashV).f('FADE', clamp(this.fade, 0, 1))
      .f('CA', f.kick * .5 + .15).f('CRT', this.crt ? (sc?.crt ?? .6) : 0).f('BS', sc?.bloom ?? .8).f('LD', ld);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
}
